import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import useBeforeunload from '../useBeforeunload';
import { RootState } from '../../redux/store';
import {
  ANALYZER_ACTION,
  ANALYZER_ENTITY,
  ANALYZER_TIME_DELAY,
  CONTENT_KEY,
} from '../../commons/constants';
import { getProject } from '../../redux/selectors/project.selector';

type UseAnalyzerProjectPropsTypes = {
  initial: boolean;
};

const useAnalyzerProject = (props: UseAnalyzerProjectPropsTypes): void => {
  const { initial } = props;

  const [isViewProject, setIsViewProject] = useState<boolean>(false);
  const [projectCode, setProjectCode] = useState<string>();
  const [prevContentActiveKey, setPrevContentActiveKey] = useState<string>();

  const contentActiveKey = useSelector((state: RootState) => state.projectModel.contentActiveKey);
  const projectKey = useSelector((state: RootState) => state.projectModel.projectKey);
  const isActivityWindow = useSelector((state: RootState) => state.layoutModel.isActivityWindow);
  // check shorten link home profile not call tracking S01-3413
  const isSelf = useSelector((state: RootState) => state.homeProfileModel.isSelf);
  const project = useSelector(getProject);

  const moduleAnalyzer = (action: string, entity: string, code = projectCode) => {
    if (window && window.ClikTracker && window.ClikTracker.sendCustomTrack && !!entity) {
      window.ClikTracker.sendCustomTrack(action, entity, {
        project_code: code,
        project_name: project.name,
      });
    }
  };

  const projectAnalyzer = (action: string, code = projectCode) => {
    if (window && window.ClikTracker && window.ClikTracker.sendCustomTrack) {
      window.ClikTracker.sendCustomTrack(action, ANALYZER_ENTITY.PROJECT, {
        project_code: code,
        project_name: project.name,
      });
    }
  };

  // run event when close tab window or reload page
  useBeforeunload(async (event) => {
    if (!initial) return;

    const promiseAll = await Promise.all([
      new Promise((resolve) => {
        if (projectCode || !isSelf) {
          projectAnalyzer(ANALYZER_ACTION.CLOSED);
        }

        resolve(true);
      }),
      new Promise((resolve) => {
        const activeContent = CONTENT_KEY[contentActiveKey];
        moduleAnalyzer(ANALYZER_ACTION.UN_LOADED, activeContent.analyzerEntity);
        resolve(true);
      }),
    ]);

    // delay close tab for waiting send data to tracking module
    const start = Date.now();
    let now = start;
    while (now - start < ANALYZER_TIME_DELAY) {
      if (promiseAll.every((i) => i === true)) now = Date.now();
    }

    // this is needed to avoid to show a confirmation prompt
    delete event['returnValue'];
  });

  useEffect(() => {
    if (!initial || !projectKey || !contentActiveKey || isSelf) return;

    // when view project call event tracking project
    if (!isViewProject && project) {
      setIsViewProject(true);
      projectAnalyzer(ANALYZER_ACTION.VIEWED, project.code);
      setProjectCode(project.code);
    }
  }, [initial, projectKey, contentActiveKey]);

  useEffect(() => {
    if (!initial || !projectKey || !contentActiveKey || !isViewProject || isSelf) return;

    if (isActivityWindow) {
      projectAnalyzer(ANALYZER_ACTION.RESUMED);
    } else {
      projectAnalyzer(ANALYZER_ACTION.INTERRUPTED);
    }
  }, [isActivityWindow]);

  useEffect(() => {
    if (!initial || !projectKey || !contentActiveKey) return;

    const activeContent = CONTENT_KEY[contentActiveKey];
    if (isActivityWindow) {
      moduleAnalyzer(ANALYZER_ACTION.RESUMED, activeContent.analyzerEntity);
    } else {
      moduleAnalyzer(ANALYZER_ACTION.INTERRUPTED, activeContent.analyzerEntity);
    }
  }, [isActivityWindow]);

  useEffect(() => {
    if (!initial) return;
    setPrevContentActiveKey(contentActiveKey);

    if (!contentActiveKey) return;
    const activeContent = CONTENT_KEY[contentActiveKey];

    if (contentActiveKey !== prevContentActiveKey) {
      if (prevContentActiveKey) {
        const prevContent = CONTENT_KEY[prevContentActiveKey];
        moduleAnalyzer(ANALYZER_ACTION.UN_LOADED, prevContent.analyzerEntity);
      }

      moduleAnalyzer(ANALYZER_ACTION.LOADED, activeContent.analyzerEntity);
    }
  }, [contentActiveKey]);
};

export default useAnalyzerProject;
